import { Button } from "@svea/design-system-components-react";
import {
	ButtonGroup,
	Dialog,
	isRequired,
	P,
	type SetOptional,
	Stack,
	Text,
	TextField,
	useText,
	validateUniqueString,
} from "@sveabank/cc-common";
import { useEffect, useState } from "react";
import { Form } from "react-final-form";
import Paper from "../../../../components/paper";
import { useApplicationId } from "../../../../hooks/useApplicationId";
import { useLazyFetchClcPersonDataQuery } from "../../../../services/clc-application";
import type { ClcBeneficialOwner, ClcPerson } from "../../../../types/clc-application";

type Props = {
	open: boolean;
	setOpen: (isOpen: boolean) => void;
	addBeneficialOwner: (
		arg: Array<SetOptional<ClcBeneficialOwner, "ownershipPercentMin" | "ownershipPercentMax">>,
	) => void;
	addedNationalIds: Array<string>;
};

type FetchPersonForm = {
	nationalId: string;
};

export const AddBeneficialOwnerModal = ({
	open,
	setOpen,
	addBeneficialOwner,
	addedNationalIds,
}: Props) => {
	const [t, texts] = useText();
	const applicationId = useApplicationId();
	const [person, setPerson] = useState<ClcPerson | null>(null);

	const onAdd = (persons: Array<ClcPerson>) => {
		addBeneficialOwner(
			persons.map((p) => ({
				...p,
				ownershipPercentMin: undefined,
				ownershipPercentMax: undefined,
				controlTypes: [],
			})),
		);
		setOpen(false);
	};

	const [fetchPersonData, { isFetching: isFetchingPerson }] = useLazyFetchClcPersonDataQuery();

	// reset form on modal close/open:
	useEffect(() => {
		setPerson(null);
	}, [open]);

	const onSubmitFetchPerson = async ({ nationalId }: FetchPersonForm) => {
		setPerson(null);
		const person = await fetchPersonData({
			applicationId,
			nationalId,
		}).unwrap();
		setPerson(person);
	};

	return (
		<Dialog
			isOpen={open}
			setOpen={setOpen}
			center
			size="small"
			heading={t(texts.newCompany.beneficialOwnership.addBeneficialOwnerModalHeading)}
		>
			<Dialog.Content>
				<Stack gap={4}>
					<Stack gap={2}>
						<Text align="start">
							{t(texts.newCompany.beneficialOwnership.addBeneficialOwnerModalText)}
						</Text>
						<Form<FetchPersonForm>
							onSubmit={onSubmitFetchPerson}
							render={({ handleSubmit }) => (
								<form onSubmit={handleSubmit}>
									<Stack gap={2} fullSize>
										<TextField
											name="nationalId"
											label={t(texts.newCompany.nationalId)}
											validators={[
												isRequired,
												validateUniqueString(addedNationalIds, texts.validation.personAlreadyAdded),
											]}
											fullWidth
										/>
										<Button
											variant={person ? "secondary" : "primary"}
											width="full"
											type="submit"
											isPending={isFetchingPerson}
										>
											{t(texts.newCompany.getPerson)}
										</Button>
									</Stack>
								</form>
							)}
						/>
					</Stack>
					{!!person && (
						<Stack gap={2}>
							<Paper className="bg-background-tertiary">
								<Text align="left">
									<P>
										<Text bold>
											{person.firstName} {person.lastName}
										</Text>
									</P>
									<P>{person.postalAddress}</P>
									<P>
										{person.postCode} {person.town}
									</P>
								</Text>
							</Paper>
						</Stack>
					)}
				</Stack>
			</Dialog.Content>
			<Dialog.Footer>
				<ButtonGroup>
					{person && <Button onPress={() => onAdd([person])}>{t(texts.common.save)}</Button>}
					<Button variant="secondary" onPress={() => setOpen(false)}>
						{t(texts.common.abort)}
					</Button>
				</ButtonGroup>
			</Dialog.Footer>
		</Dialog>
	);
};
